import { defineStore } from "pinia"
import { jwtDecode } from "jwt-decode"

import { APIAuth } from "@api/auth.api"
import Joi from "joi"

import { router } from "@router/index"

import { type APIError } from "@interfaces/APIError.interface"
import { useEventStore } from "@stores/models/event.store"
import { useSubscriptionStore } from "@stores/models/subscription.store"

// The refresh token is persisted in localStorage because Safari refuses
// the cross-site refresh cookie (app on confirmare.la, API on
// api.confirm.planific.ro), which logged clients out on every reload (PL-70)
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken"

function storeRefreshToken(refreshToken?: string) {
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken)
}

export const useAuthStore = defineStore("auth", {
  state: () => {
    return {
      contactId: null as number | null,
      accessToken: null as string | null,
      contactName: null as string | null,
      contactPhone: null as string | null,
      contactEmail: null as string | null,
      contactQrId: null as string | null,
      contactQrEnabled: false
    }
  },
  getters: {
    isAuthenticated: state => !!state.contactId
  },
  actions: {
    async validateToken(token: string): Promise<APIError | undefined> {
      const status = await APIAuth.checkIfTokenExist(
        token,
        localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
      )

      if ("error" in status) return { error: status.error }

      const {
        accessToken,
        refreshToken,
        contactName,
        contactPhone,
        contactEmail,
        contactQrId,
        contactQrEnabled
      } = status
      storeRefreshToken(refreshToken)
      this.contactName = contactName
      this.contactPhone = contactPhone
      this.contactEmail = contactEmail
      this.contactQrId = contactQrId
      this.contactQrEnabled = contactQrEnabled
      this.authentificate(accessToken)
    },
    async validatePinCode(contactId: number, pin: string) {
      const status = await APIAuth.checkIfPinExist(contactId, pin)

      if ("error" in status) return { error: status.error }

      const {
        accessToken,
        refreshToken,
        contactName,
        contactPhone,
        contactEmail,
        contactQrId,
        contactQrEnabled
      } = status
      storeRefreshToken(refreshToken)
      this.contactName = contactName
      this.contactPhone = contactPhone
      this.contactEmail = contactEmail
      this.contactQrId = contactQrId
      this.contactQrEnabled = contactQrEnabled
      this.authentificate(accessToken)
    },
    authentificate(accessToken: string, options?: { redirect?: boolean }) {
      const shouldRedirect = options?.redirect !== false

      this.accessToken = accessToken

      const { id } = jwtDecode<{ id: number }>(accessToken)
      this.contactId = id

      if (!shouldRedirect) return

      // Redirect based on available content: events have priority
      const eventStore = useEventStore()
      const subscriptionStore = useSubscriptionStore()

      let targetRouteName: "events" | "subscriptions"
      if (eventStore.hasEvents) {
        targetRouteName = "events"
      } else if (subscriptionStore.hasSubscriptions) {
        targetRouteName = "subscriptions"
      } else {
        targetRouteName = "events" // default fallback
      }

      if (router.currentRoute.value.name !== targetRouteName) {
        router.push({ name: targetRouteName })
      }
    },
    // Drop the stored session without navigating. A link that fails to log the client
    // in must not leave the previous contact's session behind, or the next visit to
    // /programari silently resumes it and greets them as somebody else (PL-243)
    forgetSession() {
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
      this.$reset()
    },
    deauthenticate() {
      this.forgetSession()
      router.push({ name: "login" })
    },
    async silentRefresh(): Promise<boolean> {
      try {
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
        const response = await APIAuth.silentRefresh(storedRefreshToken)

        const responseSchema = Joi.alternatives().try(
          Joi.object({
            accessToken: Joi.string().required(),
            refreshToken: Joi.string(),
            contactName: Joi.string().required(),
            contactPhone: Joi.string().allow("", null),
            contactEmail: Joi.string().email().allow("", null),
            contactQrId: Joi.string().required(),
            contactQrEnabled: Joi.boolean().required()
          }),
          Joi.object({
            error: Joi.string().required()
          })
        )
        const { error, value } = responseSchema.validate(response)
        if (error) throw new Error("Communication integrity error")

        const {
          accessToken,
          refreshToken,
          contactName,
          contactPhone,
          contactEmail,
          contactQrId,
          contactQrEnabled
        } = value
        storeRefreshToken(refreshToken)
        this.contactName = contactName
        this.contactPhone = contactPhone
        this.contactEmail = contactEmail
        this.contactQrId = contactQrId
        this.contactQrEnabled = contactQrEnabled
        this.authentificate(accessToken, { redirect: false })
        return true
      } catch {
        this.deauthenticate()
        return false
      }
    },
    refreshTokenMethod() {
      // TODO: Implement refresh token logic
    },
    logout() {
      try {
        APIAuth.logout(localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY))
      } finally {
        this.deauthenticate()
      }
    }
  }
})
